import React, { useRef, useState, useEffect } from 'react'
import { isEmpty } from 'lodash'
import { EduButton, notification } from '@edulastic/common'
import {
  getPDLDefaultGrade,
  getPDLDefaultSubject,
  getPDLDefaultSubjectId,
  getPDLGradeId,
} from './ducks/helpers'
import {
  StyledILPDrawer,
  StyledButtonText,
  StyledILPErrorContainer,
  StyledILPErrorHeading,
  StyledILPErrorDescription,
  StyledILPErrorButtonGroup,
} from './styled'
import { ILP_CONTENT_TYPES } from '../../../src/components/GroupAndAssign/constants'

const ILPDrawer = ({
  visible,
  setIsILPVisible,
  showILPProductSelectionScreen,
  defaultGrades = [],
  defaultSubjects = [],
  transform = {},
  onClickContentLink,
  contentTypes = [ILP_CONTENT_TYPES.PEAR_ASSESSMENT_TEST],
  pearAssessmentTestConfig = {},
}) => {
  const previousNode = useRef(null)
  const retryButtonRef = useRef(null)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    if (visible) {
      setHasError(false)
    }
  }, [visible])

  // Focus management: focus on retry button when error appears
  useEffect(() => {
    if (hasError && retryButtonRef.current) {
      setTimeout(() => {
        retryButtonRef.current?.focus()
      }, 100)
    }
  }, [hasError])

  const renderILPDrawer = (target) => {
    const showGenerateContentDrawer =
      window?.pear?.generate?.showGenerateContentDrawer

    if (showGenerateContentDrawer) {
      try {
        const pdlDefaultGrade = getPDLDefaultGrade(defaultGrades)
        const pdlDefaultGradeId = getPDLGradeId(pdlDefaultGrade)
        const pdlDefaultSubject = getPDLDefaultSubject(defaultSubjects)
        const pdlDefaultSubjectId = getPDLDefaultSubjectId(pdlDefaultSubject)

        showGenerateContentDrawer({
          target,
          onClickClose: () => setIsILPVisible(false),
          hideIncludeContextCheckbox: true,
          ...(pdlDefaultGradeId ? { gradeLevelId: pdlDefaultGradeId } : {}),
          ...(pdlDefaultSubjectId ? { subjectId: pdlDefaultSubjectId } : {}),
          ...(!isEmpty(transform) ? { transform } : {}),
          ...(typeof onClickContentLink === 'function'
            ? { onClickContentLink }
            : {}),
          contentTypes: showILPProductSelectionScreen
            ? undefined
            : contentTypes,
          expandAdvancedOptions: true,
          pearAssessmentTestConfig,
        })
        setHasError(false)
      } catch (error) {
        console.error('Error opening ILP drawer:', error)
        setHasError(true)
        notification({
          type: 'error',
          msg: 'Unable to open AI generation. Please try again.',
        })
      }
    } else {
      setHasError(true)
      notification({
        type: 'error',
        msg: 'AI generation is currently unavailable. Please try again later.',
      })
    }
  }

  const handleRetry = () => {
    setHasError(false)
    if (previousNode.current) {
      renderILPDrawer(previousNode.current)
    }
  }

  const handleCancel = () => {
    setIsILPVisible(false)
  }

  return (
    <StyledILPDrawer
      title={null}
      placement="right"
      width="650px"
      closable={false}
      onClose={() => setIsILPVisible(false)}
      visible={visible}
      destroyOnClose
      zIndex={10001}
    >
      {hasError ? (
        <StyledILPErrorContainer
          role="alert"
          aria-live="assertive"
          aria-labelledby="ilp-error-heading"
          aria-describedby="ilp-error-description"
        >
          <StyledILPErrorHeading id="ilp-error-heading">
            Unable to Load AI Generation
          </StyledILPErrorHeading>
          <StyledILPErrorDescription id="ilp-error-description">
            The AI generation service is currently unavailable. Please try again
            or cancel to go back.
          </StyledILPErrorDescription>
          <StyledILPErrorButtonGroup role="group" aria-label="Error actions">
            <EduButton
              ref={retryButtonRef}
              width="120px"
              data-cy="ilpErrorRetryButton"
              aria-label="Retry loading AI generation"
              tabIndex={0}
              onClick={handleRetry}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleRetry()
                }
              }}
            >
              <StyledButtonText>Retry</StyledButtonText>
            </EduButton>
            <EduButton
              width="120px"
              data-cy="ilpErrorCancelButton"
              aria-label="Cancel and close AI generation drawer"
              tabIndex={0}
              isGhost
              onClick={handleCancel}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleCancel()
                }
              }}
            >
              <StyledButtonText>Cancel</StyledButtonText>
            </EduButton>
          </StyledILPErrorButtonGroup>
        </StyledILPErrorContainer>
      ) : (
        <div
          ref={(node) => {
            if (node !== null && node !== previousNode.current) {
              previousNode.current = node
              renderILPDrawer(node)
            }
          }}
          style={{ width: '100%', height: '100vh' }}
        />
      )}
    </StyledILPDrawer>
  )
}

export default ILPDrawer
